import { useCallback, useEffect, useState } from 'react';
import { api } from './useApi';
import { defaultKeys, isDefaultView } from '../lib/tableColumns';

// The logged-in user's saved table views ({table: [columns]}), loaded once
// and shared by every table on screen. Saved on the server per user (and per
// property), so the same view comes back on any device.
let cache = null;      // { key, data }
let pending = null;    // { key, promise }
const listeners = new Set();

const load = (key) => {
  if (cache && cache.key === key) return Promise.resolve(cache.data);
  if (pending && pending.key === key) return pending.promise;
  const promise = api.get('/me/table-prefs')
    .then((data) => { cache = { key, data: data || {} }; return cache.data; })
    .catch(() => { cache = { key, data: {} }; return cache.data; })
    .finally(() => { pending = null; });
  pending = { key, promise };
  return promise;
};

const publish = () => listeners.forEach(fn => fn(cache ? cache.data : {}));

// cacheKey: something that changes when the user or property changes
// (e.g. `${user.id}:${propertyId}`), so a switch reloads the views.
export const useTablePrefs = (table, ctx = {}, cacheKey = '') => {
  const [all, setAll] = useState(() => (cache && cache.key === cacheKey ? cache.data : null));

  useEffect(() => {
    let alive = true;
    load(cacheKey).then(d => { if (alive) setAll(d); });
    const fn = (d) => setAll(d);
    listeners.add(fn);
    return () => { alive = false; listeners.delete(fn); };
  }, [cacheKey]);

  const saved = all ? all[table] : undefined;

  const save = useCallback(async (columns) => {
    const reset = !columns || isDefaultView(table, columns, ctx);
    const data = { ...((cache && cache.data) || {}) };
    if (reset) delete data[table]; else data[table] = columns;
    cache = { key: cacheKey, data };
    publish();
    try {
      if (reset) await api.delete(`/me/table-prefs/${table}`);
      else await api.put(`/me/table-prefs/${table}`, { columns });
    } catch (e) {
      // The view still applies on this screen; it just was not stored.
      console.error('Could not save the table view:', e);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- ctx only carries flags read at call time
  }, [table, cacheKey, ctx.multiCurrency]);

  return {
    loaded: all !== null,
    saved,
    save,
    reset: () => save(null),
    defaults: defaultKeys(table, ctx),
  };
};

export default useTablePrefs;
